import type { ResultadoRelatorio } from './RelatorioPersonalizadoService';

/* Exportar um resultado de relatório para CSV.
   Segue o que as telas antigas (ReportsPage, CampaignReportsPage) já faziam,
   porque é o arquivo que o cliente já sabe abrir: ponto e vírgula como
   separador e BOM na frente — sem o BOM o Excel em português abre "Negócios"
   como "NegÃ³cios", e sem o ponto e vírgula ele joga a linha inteira numa
   coluna só, porque nesta configuração regional a vírgula é decimal. */

/** Célula segura: aspas duplicadas, e aspas em volta quando há separador ou quebra. */
const celula = (valor: unknown): string => {
    if (valor === null || valor === undefined) return '';
    const texto = String(valor);
    return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
};

/**
 * Exporta o resultado CRU, e não o formatado na tela.
 *
 * É de propósito: "R$ 1.028,00" numa planilha é texto, e a coluna deixa de
 * somar. O número vai como número (com vírgula decimal, que é o que esta
 * configuração regional espera) e quem abrir escolhe como exibir.
 */
export function exportarResultadoCsv(resultado: ResultadoRelatorio, nome: string): void {
    const numerica = resultado.colunas.map(c => c.tipo === 'inteiro' || c.tipo === 'decimal' || c.tipo === 'percentual');

    const linhas = [
        resultado.colunas.map(c => celula(c.nome)).join(';'),
        ...resultado.linhas.map(linha => linha
            .map((v, i) => celula(numerica[i] && v !== null ? String(v).replace('.', ',') : v))
            .join(';')),
    ];

    // O total vai junto: é o número que a pessoa confere primeiro.
    if (resultado.totais.length > 0) {
        linhas.push(resultado.totais
            .map((v, i) => (i === 0 && !numerica[0] ? 'Total' : celula(numerica[i] && v !== null ? String(v).replace('.', ',') : v)))
            .join(';'));
    }

    baixar(linhas, `${arquivo(nome)}_${hoje()}.csv`);
}

/* ─── Listagens ──────────────────────────────────────────────────
   As listagens do redesign carregam a coleção inteira e filtram no navegador,
   então o arquivo sai da mesma lista que a tela acabou de filtrar — todas as
   páginas, sem API. Ver docs/designsystem/propostas/recorte-exportar-colunas.html. */

export interface ColunaCsv<T> {
    rotulo: string;
    valor: (item: T) => unknown;
    /** `numero` troca o ponto decimal por vírgula; `data` vira dd/mm/aaaa, que o
        Excel em português reconhece como data — ISO ele lê como texto. */
    tipo?: 'texto' | 'numero' | 'data' | 'telefone' | 'codigo';
}

/* Código só de dígitos (código de barras, SKU numérico) com zero na frente ou
   comprido demais o Excel estraga: tira o zero ou mostra 7,89E+12. A fórmula
   ="…" é o jeito que ele aceita de "isto é texto" num CSV. */
const codigoCsv = (v: unknown): string => {
    const t = String(v ?? '');
    return /^\d+$/.test(t) && (t.length >= 11 || t.startsWith('0')) ? `="${t}"` : t;
};

/* Telefone sai pontuado de propósito: só dígitos ("5511987654321") o Excel lê
   como número e mostra 5,51E+12. Com espaço e hífen ele fica texto, e o
   importador de planilha volta a tirar tudo que não é dígito. */
const telefoneCsv = (v: unknown): string => {
    const d = String(v ?? '').replace(/\D/g, '');
    if (d.startsWith('55') && (d.length === 12 || d.length === 13))
        return `+55 ${d.slice(2, 4)} ${d.slice(4, d.length - 4)}-${d.slice(-4)}`;
    if (d.length === 10 || d.length === 11)
        return `${d.slice(0, 2)} ${d.slice(2, d.length - 4)}-${d.slice(-4)}`;
    return String(v ?? '');
};

const dataCsv = (v: unknown): string => {
    if (!v) return '';
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('pt-BR');
};

/**
 * Exporta uma listagem já filtrada. O nome do arquivo leva a quantidade
 * (`contatos_41_2026-10-06.csv`): três exportações do mesmo dia na pasta de
 * Downloads, com recortes diferentes, precisam ser distinguíveis pelo nome.
 */
export function exportarListaCsv<T>(nome: string, itens: T[], colunas: ColunaCsv<T>[]): void {
    const linhas = [
        colunas.map(c => celula(c.rotulo)).join(';'),
        ...itens.map(item => colunas.map(c => {
            const v = c.valor(item);
            if (c.tipo === 'data') return celula(dataCsv(v));
            if (c.tipo === 'telefone') return celula(telefoneCsv(v));
            if (c.tipo === 'codigo') {
                const t = codigoCsv(v);
                return t.startsWith('="') ? t : celula(t);
            }
            if (c.tipo === 'numero' && typeof v === 'number') return celula(String(v).replace('.', ','));
            return celula(v);
        }).join(';')),
    ];
    baixar(linhas, `${arquivo(nome)}_${itens.length}_${hoje()}.csv`);
}

function baixar(linhas: string[], nomeArquivo: string): void {
    const blob = new Blob(['﻿' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/** Nome de arquivo sem acento nem caractere que o Windows recusa. */
const arquivo = (nome: string): string =>
    nome.normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase() || 'relatorio';

const hoje = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
